import { useCallback, useEffect, useState } from 'react';
import { userService } from '@/services';

/**
 * Drives the SMS phone-verification flow (Twilio Verify double opt-in) for the
 * notification settings UI: send code → enter code → verified, with a resend
 * cooldown. All network calls go through the verify edge functions; this hook
 * only owns the client-side state machine.
 */
export type PhoneVerifyStatus =
  | 'idle'
  | 'sending'
  | 'codeSent'
  | 'verifying'
  | 'verified'
  | 'error';

export const RESEND_COOLDOWN_SECONDS = 30;

function messageFrom(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export interface UsePhoneVerification {
  status: PhoneVerifyStatus;
  error: string | null;
  /** Seconds remaining before a resend is allowed (0 = allowed). */
  cooldown: number;
  start: (input: {
    e164: string;
    consentAccepted: boolean;
    consentVersion: string;
    consentText: string;
    /** Consent-event attribution — see StartPhoneVerificationInput['source']. */
    source?: 'settings_web' | 'onboarding';
  }) => Promise<boolean>;
  submitCode: (input: { e164: string; code: string }) => Promise<boolean>;
  reset: () => void;
}

export function usePhoneVerification(opts?: {
  onVerified?: () => void;
}): UsePhoneVerification {
  const onVerified = opts?.onVerified;
  const [status, setStatus] = useState<PhoneVerifyStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // Tick the resend cooldown down once per second.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const start = useCallback<UsePhoneVerification['start']>(async (input) => {
    setError(null);
    setStatus('sending');
    try {
      await userService.startPhoneVerification(input);
      setStatus('codeSent');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      return true;
    } catch (err) {
      setStatus('error');
      setError(messageFrom(err, 'Could not send the code. Please try again.'));
      return false;
    }
  }, []);

  const submitCode = useCallback<UsePhoneVerification['submitCode']>(
    async ({ e164, code }) => {
      setError(null);
      setStatus('verifying');
      try {
        const { verified } = await userService.checkPhoneVerification({
          e164,
          code,
        });
        if (verified) {
          setStatus('verified');
          onVerified?.();
          return true;
        }
        setStatus('codeSent');
        setError('That code is incorrect or expired.');
        return false;
      } catch (err) {
        setStatus('codeSent');
        setError(messageFrom(err, 'Could not verify the code.'));
        return false;
      }
    },
    [onVerified],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setCooldown(0);
  }, []);

  return { status, error, cooldown, start, submitCode, reset };
}
